import React from 'react';
import { Box, Typography, Chip, Paper, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ExpandMore, CheckCircle } from '@mui/icons-material';
import type { Role } from '../types';
import StatusChip from '@shared/components/StatusChip/StatusChip';

interface RolePermissionsDisplayProps {
  role: Role | null;
}

export const RolePermissionsDisplay: React.FC<RolePermissionsDisplayProps> = ({ role }) => {
  if (!role) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a role to view permissions
      </Typography>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: 'action.hover',
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CheckCircle color="success" fontSize="small" />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {role.name} Permissions
        </Typography>
      </Box>

      {role.module && role.module.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {role.module.map((mod) => (
            <Box
              key={mod.module_name}
              sx={{
                p: 1.5,
                backgroundColor: 'background.paper',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  color: 'primary.main',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                {mod.module_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {mod.permissions.map((permission) => (
                  <StatusChip
                    key={permission}
                    label={permission}
                    size="small"
                    variant="filled"
                    status="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No permissions assigned
        </Typography>
      )}
    </Paper>
  );
};

export default RolePermissionsDisplay;
